import type { Logger } from '@wix/thunderbolt-becky-types'

export type BuilderComponentsHierarchy = { [compType: string]: string[] }

export const getBuilderComponentsHierarchy = (
): BuilderComponentsHierarchy => {
	return {} // TODO: Implementation
}

export const getCompTypeToAllDescendants = (
	compTypeToDirectChildren: BuilderComponentsHierarchy,
	logger: Logger
): BuilderComponentsHierarchy => {
	const result: BuilderComponentsHierarchy = {}
	const cache: Record<string, string[]> = {}

	const componentExists = (compType: string): boolean => {
		if (compType in compTypeToDirectChildren) {
			return true
		}
		logger.warn(`Component ${compType} does not exist in builder components hierarchy`)
		return false
	}

	const getValidChildren = (compType: string): string[] => {
		const directChildren = compTypeToDirectChildren[compType] || []
		return directChildren.filter((child) =>
			componentExists(child) && child !== compType
		)
	}

	const calculateDescendants = (compType: string, currentPath: string[] = []): string[] => {
		if (cache[compType]) {
			return cache[compType]
		}

		if (currentPath.includes(compType)) {
			return []
		}

		const validChildren = getValidChildren(compType)
		if (validChildren.length === 0) {
			cache[compType] = []
			return []
		}

		const allDescendants = new Set<string>()
		const newPath = [...currentPath, compType]

		// Add all descendants of each child
		validChildren.forEach((child) => {
			validChildren.forEach((child) => allDescendants.add(child))
			const childDescendants = calculateDescendants(child, newPath)
			childDescendants.forEach((descendant) => allDescendants.add(descendant))
		})

		const descendants = Array.from(allDescendants)
		cache[compType] = descendants
		return descendants
	}

	// Calculate descendants for all components
	Object.keys(compTypeToDirectChildren).forEach((compType) => {
		result[compType] = calculateDescendants(compType)
	})

	// Handle cycles: find all strongly connected components
	const findCycleMembers = (startComponent: string): string[] => {
		const cycleMembers = new Set<string>()
		const toCheck = [startComponent]

		while (toCheck.length > 0) {
			const current = toCheck.pop()!
			if (cycleMembers.has(current)) continue

			// If current can reach startComponent and startComponent can reach current, they're in the same cycle
			if (result[current]?.includes(startComponent) && result[startComponent]?.includes(current)) {
				cycleMembers.add(current)
				// Add all descendants of this cycle member to check
				result[current]?.forEach((descendant) => {
					if (!cycleMembers.has(descendant)) {
						toCheck.push(descendant)
					}
				})
			}
		}

		return Array.from(cycleMembers)
	}

	// For each component, check if it's in a cycle and update all cycle members
	const processedCycles = new Set<string>()
	Object.keys(result).forEach((compType) => {
		if (processedCycles.has(compType)) {
			return
		}

		const cycleMembers = findCycleMembers(compType)
		if (cycleMembers.length > 1) {
			// All cycle members should contain all other cycle members as descendants
			cycleMembers.forEach((member) => {
				const currentDescendants = new Set(result[member])
				cycleMembers.forEach((otherMember) => {
					if (member !== otherMember) {
						currentDescendants.add(otherMember)
					}
				})
				result[member] = Array.from(currentDescendants)
				processedCycles.add(member)
			})
		}
	})

	return result
}
